import React, { createContext, useState, useContext, useCallback } from 'react';
import { useAuth } from '../auth';
import { getNotifications, markNotificationsAsVisible} from '../../client/notifications';

const NotificationsContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications debe usarse dentro de NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    const refreshNotifications = useCallback(async () => {
        if (!user?.supplierId) return;
        setLoading(true);
        try {
            const response = await getNotifications(user.supplierId, 1, 10);
            setNotifications(response.notifications || []);
            setUnreadNotifications(response.unreadNotifications || []);
            setTotalCount(response.pagination?.totalNotifications || 0);
            setUnreadCount(response.unreadCount || 0);
        } catch (error) {
            console.error('Error al refrescar notificaciones:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.supplierId]);

  const handleWebSocketMessage = useCallback((message) => {

    const { EventType, Payload } = message;

    // Eventos que agregan notificaciones al panel
    const notificationEvents = [
      'NuevaEncuestaAsignada',
      'RecordatorioEncuesta',
      'EvaluacionRegistrada',
      'ManualCreado',
      'TemporadaAsignada',
      'TemporadaRetirada',
      'DocumentoProveedorActualizado',
      'CitaActualizada',
      'EstadoOCActualizado'
    ];

    if (notificationEvents.includes(EventType) && Payload) {
      const newNotification = {
        id: Payload.id,
        title: Payload.title,
        description: Payload.description,
        moduleId: Payload.moduleId,
        visible: false,
        created_At: Payload.created_At
      };
      setNotifications(prev => [newNotification, ...prev].slice(0, 50));
      setUnreadNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
    }

    // NotificacionJob - actualiza órdenes de compra
    if (EventType === 'NotificacionJob' && Payload) {
      window.dispatchEvent(new CustomEvent('ocs-updated', { detail: Payload }));
    }

    // HorarioActualizado - actualiza cupos
    if (EventType === 'HorarioActualizado') {
      window.dispatchEvent(new CustomEvent('schedule-updated', { detail: Payload }));
    }

  }, []);

    const markAsRead = useCallback(async (notificationIds) => {
        if (!notificationIds?.length) return;
        
        try {
            const response = await markNotificationsAsVisible(notificationIds);
            
            setNotifications(prev => prev.map(n => 
                response.updatedIds.includes(n.id) ? { ...n, visible: true } : n
            ));
            setUnreadNotifications(prev => prev.filter(n => !response.updatedIds.includes(n.id)));
            setUnreadCount(prev => Math.max(0, prev - response.updatedCount));
            
            return response;
        } catch (error) {
            console.error('Error al marcar notificaciones como leídas:', error);
        }
    }, []);

    return (
        <NotificationsContext.Provider value={{
            notifications,
            unreadNotifications,
            totalCount,
            unreadCount,
            loading,
            refreshNotifications,
            markAsRead,
            handleWebSocketMessage
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};